import { useEffect, useState } from 'react'
import { motion, useReducedMotion } from 'framer-motion'
import { formatCurrency, formatCurrencyCompact, formatPercent } from '@/utils/format-currency'
import { cn } from '@/utils/cn'

export type KpiValueFormat = 'currency' | 'percent' | 'integer' | 'text'

/** Duração da contagem do valor (ms). */
export const KPI_VALUE_ANIMATION_MS = 3000

function formatAnimatedValue(
  value: number,
  format: KpiValueFormat,
  compactCurrency: boolean,
): string {
  switch (format) {
    case 'currency':
      return compactCurrency ? formatCurrencyCompact(value) : formatCurrency(value)
    case 'percent':
      return formatPercent(value)
    case 'integer':
      return new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 0 }).format(
        Math.round(value),
      )
    default:
      return String(value)
  }
}

interface AnimatedKpiValueProps {
  numericValue?: number
  displayValue: string
  format?: KpiValueFormat
  className?: string
  delay?: number
  compactCurrency?: boolean
}

export function AnimatedKpiValue({
  numericValue,
  displayValue,
  format = 'text',
  className,
  delay = 0,
  compactCurrency = false,
}: AnimatedKpiValueProps) {
  const reduced = useReducedMotion()
  const [text, setText] = useState(() =>
    reduced || numericValue == null
      ? displayValue
      : formatAnimatedValue(0, format, compactCurrency),
  )

  useEffect(() => {
    if (numericValue == null || format === 'text') {
      setText(displayValue)
      return
    }

    if (reduced) {
      setText(formatAnimatedValue(numericValue, format, compactCurrency))
      return
    }

    const target = numericValue
    const duration = KPI_VALUE_ANIMATION_MS
    const startAt = performance.now() + delay

    let frame = 0
    const tick = (now: number) => {
      if (now < startAt) {
        frame = requestAnimationFrame(tick)
        return
      }
      const elapsed = now - startAt
      const progress = Math.min(1, elapsed / duration)
      const eased = 1 - (1 - progress) ** 3
      setText(formatAnimatedValue(target * eased, format, compactCurrency))
      if (progress < 1) frame = requestAnimationFrame(tick)
    }

    setText(formatAnimatedValue(0, format, compactCurrency))
    frame = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(frame)
  }, [numericValue, displayValue, format, reduced, delay, compactCurrency])

  const shown =
    numericValue != null && format !== 'text' ? text : displayValue

  return (
    <div className={cn('min-w-0 overflow-hidden', className)}>
      <motion.p
        key={reduced ? displayValue : `kpi-${numericValue}`}
        initial={reduced ? false : { y: '100%', opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{
          duration: 0.5,
          delay: delay / 1000,
          ease: [0.22, 1, 0.36, 1],
        }}
        className='truncate text-xl font-bold leading-none tracking-tight text-[var(--font-color-h1)] tabular-nums sm:text-2xl'
      >
        {shown}
      </motion.p>
    </div>
  )
}
